'use client';

import { useTransition } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { Lock } from 'lucide-react';

import { ChannelHeader } from '../../community-channels/components/channel-detail/ChannelHeader';
import { ChannelJoinErrorDialog } from '../../community-channels/components/channel-detail/ChannelJoinErrorDialog';
import { useCommunityContext } from '../../community-channels/contexts/community/useCommunityContext';
import { useJoinChannel } from '../../community-channels/hooks/useJoinChannel';
import type { CommunityChannel } from '../../community-channels/types/CommunityChannel';
import { channelSubscribeHref } from './channelSubscribeHref';

interface Props {
  channel: CommunityChannel;
  bioContext: BioContext;
  /**
   * Whether the viewer meets the channel's subscription requirement (so a join
   * is all that's left). `false` → they must subscribe first.
   */
  hasSubscriptionAccess: boolean;
}

/**
 * The private-channel blocker a visitor sees before they've joined — the web port
 * of PopshopRN's `CommunityChannelBlockerView` / "Join Channel" alert. Keeps the
 * channel header (so Back works) and offers:
 *
 *   - already subscribed → "Join Channel Now!" (joins in place; the live
 *     membership subscription then swaps in the message pane)
 *   - not subscribed → "Subscribe Now and Join Channel" (to the subscribe flow)
 */
export function ChannelJoinGate({
  channel,
  bioContext,
  hasSubscriptionAccess,
}: Props) {
  const router = useRouter();
  const { homeHref } = useCommunityContext();
  const { join, joining, error, clearError } = useJoinChannel(channel);
  const [pending, startTransition] = useTransition();

  const onJoin = () => {
    startTransition(async () => {
      await join();
    });
  };

  const busy = joining || pending;

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <ChannelHeader channel={channel} withBackButton />
      <div className="flex flex-1 flex-col items-center justify-center gap-5 p-6 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/5">
          <Lock className="h-9 w-9 text-white/40" aria-hidden />
        </div>
        <div className="flex max-w-[420px] flex-col gap-2">
          <h2 className="text-xl font-bold text-white">Join Channel</h2>
          <p className="text-checkout-neutral-300 text-sm">
            {hasSubscriptionAccess
              ? "This channel is private, so you'll need to join first to access this exclusive space and chat with them."
              : 'This channel is private, so you need to subscribe & join first to access this exclusive space and chat with them.'}
          </p>
        </div>

        <div className="flex w-full max-w-[360px] flex-col items-center gap-3">
          {hasSubscriptionAccess ? (
            <button
              type="button"
              onClick={onJoin}
              disabled={busy}
              className="bg-mint-green-300 hover:bg-mint-green-400 w-full rounded-full px-4 py-3 text-sm font-bold text-black transition disabled:opacity-60"
            >
              {busy ? 'Joining…' : 'Join Channel Now!'}
            </button>
          ) : (
            <Link
              href={channelSubscribeHref(bioContext, channel)}
              className="bg-mint-green-300 hover:bg-mint-green-400 w-full rounded-full px-4 py-3 text-sm font-bold text-black transition"
            >
              Subscribe Now and Join Channel
            </Link>
          )}
          <button
            type="button"
            onClick={() => router.push(homeHref)}
            className="text-checkout-neutral-400 text-sm font-medium transition hover:text-white"
          >
            I don&apos;t want to join the fun!
          </button>
        </div>

        <ChannelJoinErrorDialog error={error} onClose={clearError} />
      </div>
    </section>
  );
}
